import {
  DianArticle,
  DianPurchase,
  DianPurchaseAdjustmentNote,
  RefundValue,
} from '@shared-models';

import { IArticles } from 'src/app/@interfaces/article.interface';
import {
  IRefundsSingle,
  IRefundsValuesSingle,
} from 'src/app/@interfaces/refunds.interface';

import { PurchaseMapper } from './purchase.mapper';

export class PurchaseAdjustmentNoteMapper {
  static mapper(
    refund: IRefundsSingle,
    purchase: DianPurchase & {
      electronic_support: {
        document_number: string;
        cuds: string;
        issue_date?: string;
      };
    },
    adjustmentDocumentNumber: string,
  ): DianPurchaseAdjustmentNote {
    // Normalise the nested purchase (provider.types_docs / country / lines)
    // exactly as the DS flow does — Api-Dian Provider() expects types_docs as
    // an object, but the GraphQL response returns it as an array because of
    // the User-DB association include. PurchaseMapper unwraps array→object.
    const mappedPurchase = PurchaseMapper.mapper(purchase) as DianPurchase & {
      electronic_support: {
        document_number: string;
        cuds: string;
        issue_date?: string;
      };
    };

    const issueDate =
      mappedPurchase.electronic_support.issue_date ||
      PurchaseAdjustmentNoteMapper.toIsoDate(purchase.createdAt) ||
      '';

    return {
      id_refund: refund.id_refund,
      num_invo: refund.num_invo,
      refund_type: refund.refund_type,
      concept: String(refund.concept),
      total: Number(refund.total),
      withholdingTax: Number(refund.withholdingTax ?? 0),
      icaTax: Number(refund.icaTax ?? 0),
      createAt: refund.createAt,
      dueDate: refund.dueDate,
      currency: refund.currency || mappedPurchase.currency || 'COP',
      tax: refund.tax || mappedPurchase.tax,
      refunds_values: (refund.refunds_values ?? []).map((value) =>
        PurchaseAdjustmentNoteMapper.mapLine(
          value,
          mappedPurchase.purchases ?? [],
        ),
      ),
      purchase: {
        ...mappedPurchase,
        electronic_support: {
          ...mappedPurchase.electronic_support,
          issue_date: issueDate,
        },
      },
      electronic_support_adjustment: {
        document_number: adjustmentDocumentNumber,
      },
    };
  }

  /**
   * DIAN BillingReference IssueDate must be YYYY-MM-DD. `electronic_support`
   * does not persist the original DS issue date, so we derive it from
   * `purchase.createdAt` (closest stored proxy — the purchase was emitted to
   * DIAN around its creation date). Accepts `MM/DD/YYYY` (legacy text columns)
   * and ISO strings; returns an empty string for unparseable input so the
   * caller's `||` fallback can decide.
   */
  private static toIsoDate(value: unknown): string {
    if (typeof value !== 'string' || !value) return '';
    const mdy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mdy) {
      const [, m, d, y] = mdy;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const iso = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
    return '';
  }

  private static mapLine(
    value: IRefundsValuesSingle,
    purchaseLines: { codigo?: string; purchase_unit?: unknown }[] = [],
  ): RefundValue {
    // RefundsValues.belongsToMany → articulo arrives as an array [{}] from
    // GraphQL (declared as `articulo: [Articulos]` in the schema). Unwrap to
    // the first element before normalising internal arrays (tax/type).
    const rawArticulo = Array.isArray(value.articulo)
      ? value.articulo[0]
      : value.articulo;
    const articulo = PurchaseAdjustmentNoteMapper.mapArticulo(
      rawArticulo,
      PurchaseAdjustmentNoteMapper.findPurchaseUnit(
        value,
        rawArticulo,
        purchaseLines,
      ),
    );

    const taxRate = Number(articulo?.tax?.value ?? 0);

    const quantity = Math.abs(Number(value.quantity));
    const price = Math.abs(Number(value.price));
    const discount = Math.abs(Number(value.discount ?? 0));
    const subtotal = Math.abs(Number(value.subtotal ?? quantity * price));
    const discounted = subtotal - (discount / 100) * subtotal;
    const total = +(discounted * (1 + taxRate)).toFixed(2);

    // PurchaseItem.__init__ does `kwargs.get("warehouse", {}).get("id_almacen")`.
    // If GraphQL returns warehouse=null, the .get("warehouse", {}) default does
    // NOT kick in (key exists, value is None) → None.get(...) crashes Api-Dian.
    const warehouse = value.warehouse ?? { id_almacen: 0, nombre: '' };

    return {
      id_value: value.id_value!,
      codigo: value.codigo ?? articulo?.codigo ?? '',
      quantity,
      price,
      subtotal,
      discount,
      total,
      warehouse,
      articulo,
    };
  }

  /**
   * An adjustment note line must be reported in the same unit as the purchase
   * line it adjusts, since its quantity comes from that line. `refunds_values`
   * stores no link to the purchase line, so the match is by article code —
   * the only key both sides share.
   *
   * Unlike invoices, purchases are not guarded against repeating an article
   * (there is no equivalent of ArticleValidationsService
   * .checkDuplicateArticles on the purchase screens), so a purchase carrying
   * the same article twice in different units would be ambiguous here and the
   * first match wins. Resolving that properly needs the purchase line id on
   * refunds_values.
   */
  private static findPurchaseUnit(
    value: IRefundsValuesSingle,
    article: IArticles | undefined,
    purchaseLines: { codigo?: string; purchase_unit?: unknown }[],
  ): DianArticle['unit'] | undefined {
    const code = value.codigo ?? article?.codigo;
    if (!code) return undefined;

    const line = purchaseLines.find((item) => item.codigo === code);
    const unit = line?.purchase_unit as
      | { description?: string; dian_code?: string }
      | undefined;

    if (!unit?.dian_code) return undefined;

    return {
      description: unit.description ?? '',
      dian_code: unit.dian_code,
    };
  }

  private static mapArticulo(
    article: IArticles | undefined,
    purchaseUnit?: DianArticle['unit'],
  ): DianArticle | undefined {
    if (!article) return article;
    const tax = Array.isArray(article.tax) ? article.tax[0] : article.tax;
    const type = Array.isArray(article.type) ? article.type[0] : article.type;
    return {
      codigo: article.codigo ?? '',
      descripcion: article.descripcion,
      tax: tax
        ? {
            abbreviation: tax.abbreviation ?? '',
            type: tax.type ?? '',
            value: tax.value != null ? tax.value.toString() : '0',
            dian_code: tax.dian_code ?? '01',
          }
        : { abbreviation: '', type: '', value: '0', dian_code: '01' },
      type: type
        ? { description: type.description, dian_code: type.dian_code }
        : { description: '', dian_code: '' },
      // REFUND_OBJECT fragment now fetches articulo.unit { description,
      // dian_code } so Api-Dian Article(**kwargs) builds ArticleUnit cleanly.
      // The purchase unit takes precedence when the adjusted purchase line
      // carries one: the quantity is expressed in it, so the reported unit
      // has to match. Falls back to the article's base unit otherwise.
      unit:
        purchaseUnit ??
        (article.unit
          ? {
              description: article.unit.description,
              dian_code: article.unit.dian_code,
            }
          : { description: '', dian_code: '' }),
    };
  }
}
